import React, { useEffect, useState } from "react";
import { Row, Col, Button, Flex, Grid } from "antd";

const { useBreakpoint } = Grid;
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import CreateServiceExpenseForm from "./components/CreateServiceExpenseForm";
import ServiceExpenseTable from "./components/ServiceExpenseTable";
import { createServiceExpense, fetchServiceExpenseNumber } from "./serviceExpenseThunk";
import { resetServiceExpense, setFormField } from "./serviceExpenseSlice";
import Swal from "sweetalert2";
import { selectAuthInfo } from "../../../Auth/authSelectors";
import { getAccessType } from "../../../../lib/functions";

const ServiceExpenseEntry = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const authInfo = useSelector(selectAuthInfo);
  const screens = useBreakpoint();
  const isMobile = !screens.sm;

  let [access_type, access_type_set] = useState("");

  useEffect(() => {
    const access = getAccessType("service_expense_entry");
    access_type_set(access);
  }, []);

  useEffect(() => {
    return () => {
      dispatch(resetServiceExpense());
    };
  }, [dispatch]);

  const { itemCart, supplierInfo, narration, voucherNumber,
    payServiceCart,
    selectedServiceExpenseAcc,
    selectedDiscountAcc,
    selectedTaxAcc,
    selectedTransportAcc,
    footerValues,
  } =
    useSelector((s) => s.serviceExpense);

  function sanitizeServiceItems(items) {
    return items.filter(
      (row) => row.item_id && Number(row.quantity) > 0
    );
  }

  function validateDuplicateServiceItems(items) {
    const seen = new Set();

    for (const row of items) {
      const key = [
        row.item_id,
        Number(row.rate || 0).toFixed(2),
        Number(row.tax_per || 0),
        Number(row.discount || 0)
      ].join("|");

      if (seen.has(key)) {
        return {
          valid: false,
          message: `Duplicate service detected: ${row.product}`,
        };
      }
      seen.add(key);
    }

    return { valid: true };
  }

  // Save handler
  const handleSave = () => {

    const cleanedItems = sanitizeServiceItems(itemCart);

    if (cleanedItems.length === 0) {
      Swal.fire(
        "Invalid Service Entry",
        "Please add at least one service with quantity.",
        "warning"
      );
      return;
    }

    const { valid, message } =
      validateDuplicateServiceItems(cleanedItems);

    if (!valid) {
      Swal.fire("Duplicate Service Item", message, "warning");
      return;
    }

    const {
      description: narration,
      subtotal,
      totalTax,
      grandTotal,
      total_discount,
      description,
      paidAmount,
      dueAmount,
      serviceCharge,
      discountPct,
      taxPct,
    } = footerValues;


    const convertedTotal = (payServiceCart || []).reduce(
      (sum, p) => sum + parseFloat(p.tran_amount || p.amount || 0),
      0
    );

    const hasConversionRate = Number(footerValues?.conversion_rate) > 0;
    
    const maxAllowedCartTotal = hasConversionRate
      ? Number(footerValues.grandTotal) + 0.1
      : Number(footerValues.grandTotal);



    dispatch(
      setFormField({
        field: "itemCart",
        value: cleanedItems,
      })
    );

    if (!supplierInfo || !supplierInfo.acc_id) {
      Swal.fire("Select supplier!", "", "warning");
      return;
    }

    if (!itemCart || itemCart.length === 0) {
      Swal.fire("Item cart is Empty!", "", "warning");
      return;
    }

    if (narration && narration.length > 255) {
      Swal.fire("Description max size exceeded", "", "warning");
      return;
    }
    if (itemCart.length === 0) {
      Swal.fire("Item cart is empty!");
      return;
    }
    if (!selectedServiceExpenseAcc) {
      Swal.fire("Select Services Account!");
      return;
    }
    if (!selectedDiscountAcc) {
      Swal.fire("Discount Account is required!");
      return;
    }
    if (!selectedTaxAcc) {
      Swal.fire("VAT Account is required!");
      return;
    }
    if (!supplierInfo?.acc_id) {
      Swal.fire("Select Customer!");
      return;
    }
    if (supplierInfo.institution_name && supplierInfo.institution_name.length > 100) {
      Swal.fire("Institution Name size exceeded!");
      return;
    }
    if (supplierInfo.address && supplierInfo.address.length > 200) {
      Swal.fire("Address size exceeded!");
      return;
    }
    if (!supplierInfo.contact_no || supplierInfo.contact_no.length <= 0) {
      Swal.fire("Contact No is required!");
      return;
    }
    if (paidAmount && (Number(convertedTotal) !== paidAmount)) {
      Swal.fire(
        "Paid Amount & Payment Cart Amount should be same!"
      );
      return;
    }

    if (Number(convertedTotal.toFixed(2)) > Number(maxAllowedCartTotal.toFixed(2))) {      
        Swal.fire({
        title: "Payment amount exceeds total!",
        text: `Cart Total (${convertedTotal.toFixed(
          2
        )}) should not exceed Grand Total (${Number(footerValues.grandTotal).toFixed(2)})`,
        icon: "warning",
      });
      return;
    }

    Swal.fire({
      title: "Saving Service Expense...",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });
    const isEdit = !!id;
    dispatch(createServiceExpense())
      .unwrap()
      .then((res) => {
        Swal.close();
        if (!res.error) {
          Swal.fire({
            title: isEdit
              ? "Service Expense Updated! Do you want to view invoice?"
              : "Service Expense Created! Do you want to view invoice?",
            showCancelButton: true,
            confirmButtonText: "Yes, view invoice",
            cancelButtonText: "No, go back",
          }).then((result) => {
            if (result.isConfirmed) {
              dispatch(resetServiceExpense());
              navigate(`/service/quick-service-expense-voucher/${res.service_ex_id}`);
            } else {
              dispatch(resetServiceExpense());
              navigate("/service-expense");
              window.location.reload();
            }
          });
        } else {
          Swal.fire(res.message || "Something went wrong", "", "warning");
        }
      })
      .catch(() => {
        Swal.close();
        Swal.fire("Network ERROR", "", "error");
      });
  };

  const handleBack = () => {
    if (location.state?.from) {
      navigate(location.state.from);
    } else {
      navigate(-1);
    }
  };

  const handleReset = () => {
    dispatch(resetServiceExpense());
    Swal.fire("Form Reset", "All fields and items cleared", "info");
    dispatch(fetchServiceExpenseNumber());
  };

  const ActionButtons = () => (
    <Flex justify="flex-end" wrap="wrap" gap={16} style={{ marginBottom: 16 }}>
      <Button onClick={handleBack} style={isMobile ? { flex: 1 } : undefined}>Back</Button>
      <Button onClick={handleReset} style={isMobile ? { flex: 1 } : undefined}>Reset</Button>
      {access_type == "WRITE" && <>
        <Button type="primary" style={isMobile ? { flex: 1 } : undefined} onClick={handleSave} data-testid="final-save-btn">
          Save
        </Button>
      </>}
    </Flex>
  );

  return (
    <div style={{ padding: isMobile ? 10 : 20 }}>
      <Row>
        <Col span={24}>
          <ActionButtons />
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <CreateServiceExpenseForm editId={id} authInfo={authInfo} />
        </Col>
        <Col span={24}>
          <ServiceExpenseTable authInfo={authInfo} />
        </Col>
      </Row>

      <Row style={{ marginTop: 24 }}>
        <Col span={24}>
          <ActionButtons />
        </Col>
      </Row>
    </div>
  );
};

export default ServiceExpenseEntry;
